/**
 * Reschedule Requests service — READ-ONLY access to `rescheduleRequests`.
 *
 * A request is the mentor→student approval flow for moving a class:
 *   {
 *     classId, classTitle, seriesId|null, bucketKey,
 *     studentId, mentorId,                    // denormalized for rules scoping
 *     currentStartAt, currentTimezone, durationMin,
 *     proposedStartAt, proposedTimezone,
 *     reason,
 *     status: 'pending'|'accepted'|'rejected'|'expired'|'superseded'|'cancelled',
 *     expiresAt,                              // == class startAt at creation
 *     resolved: boolean,                      // sweep bookkeeping
 *     requestedByName, respondedAt|null, responseNote,
 *     quotaAtRequest: { used, limit, overQuota },
 *     outcome: { makeupClassId?, penaltyId?, historyId? },
 *     createdAt, updatedAt
 *   }
 *
 * ALL writes happen inside the scheduling callables (rules: `write: false`) so
 * supersede semantics, quota snapshots and expiry stay server-authoritative.
 */
import firestoreService, { where, orderBy, limit } from './firestore.service';

const COLLECTION = 'rescheduleRequests';

export const REQUEST_STATUS = Object.freeze({
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  EXPIRED: 'expired',
  SUPERSEDED: 'superseded',
  CANCELLED: 'cancelled',
});

export const rescheduleRequestsService = {
  /** All of a student's requests, newest first (optionally one status). */
  subscribeByStudent(studentId, onData, onError, { status, max = 100 } = {}) {
    const constraints = [where('studentId', '==', studentId)];
    if (status) constraints.push(where('status', '==', status));
    constraints.push(orderBy('createdAt', 'desc'), limit(max));
    return firestoreService.subscribeCollection(COLLECTION, constraints, onData, onError);
  },

  /** All of a mentor's requests, newest first (optionally one status). */
  subscribeByMentor(mentorId, onData, onError, { status, max = 100 } = {}) {
    const constraints = [where('mentorId', '==', mentorId)];
    if (status) constraints.push(where('status', '==', status));
    constraints.push(orderBy('createdAt', 'desc'), limit(max));
    return firestoreService.subscribeCollection(COLLECTION, constraints, onData, onError);
  },

  /** The request thread for one class, newest first. */
  subscribeByClass(classId, onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('classId', '==', classId), orderBy('createdAt', 'desc')],
      onData,
      onError
    );
  },

  /** Admin: every pending request platform-wide. */
  subscribeAllPending(onData, onError) {
    return firestoreService.subscribeCollection(
      COLLECTION,
      [where('status', '==', REQUEST_STATUS.PENDING)],
      onData,
      onError
    );
  },

  get(id) {
    return firestoreService.getDocument(COLLECTION, id);
  },
};

export default rescheduleRequestsService;
